import React, {useState, useEffect, useCallback} from 'react'
import {Formik, Form} from 'formik'
import DataTable, {TableColumn, TableAction} from '../../../../shared/components/DataTable' 
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {IBafModel} from './BafModel'
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import {AsyncCarModel} from '../../../../shared/models/ApiModels'
import {fetchAsyncCarData} from '../../../../shared/models/SharedApiLookups'

type Props = {
  data: IBafModel[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onEdit?: (bafEntry: IBafModel) => void
  onDelete?: (bafEntry: IBafModel) => void
  loading?: boolean
  emptyMessage?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: string, order: 'asc' | 'desc') => void
}

const officeOptions = ['ANKARA ÇANKAYA', 'ANTALYA AİRPORT', 'BURSA', 'DENİZLİ']

export const BafTable: React.FC<Props> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  onEdit,
  onDelete,
  loading = false,
  emptyMessage = 'Henüz BAF kaydı bulunamadı',
  sortBy,
  sortOrder,
  onSort,
}) => {
  const [tableData, setTableData] = useState<IBafModel[]>(data || [])
  const [ignoreDate, setIgnoreDate] = useState(false)
  const [ignoreOffice, setIgnoreOffice] = useState(false)

  useEffect(() => {
    setTableData(data)
  }, [data])

  const handleFilter = useCallback(
    (values: Record<string, any>) => {
      let filtered = [...data]

      if (values.personnel) {
        filtered = filtered.filter((item) =>
          item.personnelName?.toLowerCase().includes(values.personnel.toLowerCase())
        )
      }

      if (!ignoreDate && values.startDate && values.endDate) {
        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.operationDate).getTime()
          const start = new Date(values.startDate).getTime()
          const end = new Date(values.endDate).getTime()
          return itemDate >= start && itemDate <= end
        })
      }

      if (values.plate) {
        filtered = filtered.filter((item) =>
          item.vehicleType?.toLowerCase().includes(values.plate.toLowerCase())
        )
      }

      if (!ignoreOffice && values.officeSelection) {
        filtered = filtered.filter((item) => item.departureBranch === values.officeSelection)
      }

      if (values.status && values.status !== 'Hepsi') {
        filtered = filtered.filter((item) => item.status === values.status)
      }

      if (values.usagePurpose && values.usagePurpose !== 'Hepsi') {
        filtered = filtered.filter((item) => item.userPurpose === values.usagePurpose)
      }

      setTableData(filtered)
    },
    [data, ignoreDate, ignoreOffice]
  )

  const handleSortLocal = (column: string, order: 'asc' | 'desc') => {
    if (onSort) {
      onSort(column, order)
    } else {
      const sorted = [...tableData].sort((a, b) => {
        const aVal = (a as any)[column] ?? ''
        const bVal = (b as any)[column] ?? ''
        if (aVal < bVal) return order === 'asc' ? -1 : 1
        if (aVal > bVal) return order === 'asc' ? 1 : -1
        return 0
      })
      setTableData(sorted)
    }
  }

  // Tablo kolonları, en başa kayıt no eklendi
  const bafColumns: TableColumn<IBafModel>[] = [
    {
      key: 'recordNo',
      label: 'Kayıt No',
      sortable: false,
      render: (item: IBafModel, value: any) => tableData.indexOf(item) + 1,
    },
    {key: 'operationDate', label: 'İşlem Tarihi', sortable: true},
    {key: 'returnDate', label: 'Baş. Tarihi', sortable: true},
    {key: 'returnTime', label: 'Baş. Saati', sortable: true},
    {key: 'processedByFullName', label: 'İşlemi Yapan', sortable: true},
    {key: 'vehicleType', label: 'Araç Tipi'},
    {key: 'departureBranch', label: 'Çıkış Şubesi'},
    {key: 'status', label: 'Durum'},
  ]

  const bafActions: TableAction<IBafModel>[] = [
    ...(onEdit
      ? [
          {
            label: 'Düzenle',
            icon: 'bi bi-pencil',
            className: 'btn-light-primary me-2',
            onClick: onEdit,
          },
        ]
      : []),
    ...(onDelete
      ? [{label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: onDelete}]
      : []),
  ]

  return (
    <div className='card'>
      <div className='card-body'>
        <Formik
          initialValues={{
            personnel: '',
            startDate: '',
            endDate: '',
            plate: '',
            status: 'Hepsi',
            officeSelection: '',
            usagePurpose: 'Hepsi',
          }}
          onSubmit={handleFilter}
        >
          {({values, setFieldValue}) => (
            <Form>
              <div className='row mb-6'>
                <AsyncSearchInput<AsyncCarModel>
                  name="carId"
                  label='Araç Ara'
                  placeholder='Araç bilgisi girin...'
                  fetchHandler={fetchAsyncCarData}
                  displayField={(item) => `${item.plate} (${item.modelYear})`}
                  onSelect={(item) => console.log('Seçilen araç:', item)}
                  onClear={() => console.log('Araç temizlendi')}
                />
                <div className='col-md-6'>
                  <div className='row'>
                    <div className='col-4'>
                      <CheckboxInput
                        name='ignoreDate'
                        label='Tarih Önemsiz'
                        checked={ignoreDate}
                        onChange={() => setIgnoreDate(!ignoreDate)}
                      />
                    </div>
                    <div className='col-4'>
                      <TextInput
                        name='startDate'
                        type='date'
                        disabled={ignoreDate}
                        value={values.startDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('startDate', e.target.value)
                        }
                      />
                    </div>
                    <div className='col-4'>
                      <TextInput
                        name='endDate'
                        type='date'
                        disabled={ignoreDate}
                        value={values.endDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('endDate', e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row mb-3'>
                <AsyncSearchInput<AsyncCarModel>
                  name="carId1"
                  label='Araç Ara'
                  placeholder='Araç bilgisi girin...'
                  fetchHandler={fetchAsyncCarData}
                  displayField={(item) => `${item.plate} (${item.modelYear})`}
                  onSelect={(item) => console.log('Seçilen araç:', item)}
                  onClear={() => console.log('Araç temizlendi')}
                />
                <div className='col-md-6'>
                  <div className='row align-items-end'>
                    <div className='col-auto'>
                      <CheckboxInput
                        name='ignoreOffice'
                        label='Ofis Önemsiz'
                        checked={ignoreOffice}
                        onChange={() => setIgnoreOffice(!ignoreOffice)}
                      />
                    </div>
                    <div className='col'>
                      <SelectInput
                        name='officeSelection'
                        label='Ofis Seç'
                        options={officeOptions.map((o) => ({value: o, label: o}))}
                        disabled={ignoreOffice}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row mb-3'>
                <div className='col-md-6'>
                  <div className='row mb-3'>
                    <div className='col-md-6'>
                      <SelectInput
                        name='status1'
                        label='Durum 1'
                        options={[
                          {value: 'Devam Ediyor', label: 'Devam Ediyor'},
                          {value: 'Tamamlandı', label: 'Tamamlandı'},
                          {value: 'Hepsi', label: 'Hepsi'},
                        ]}
                      />
                    </div>
                    <div className='col-md-6'>
                      <SelectInput
                        name='status2'
                        label='Durum 2'
                        options={[
                          {value: 'Devam Ediyor', label: 'Devam Ediyor'},
                          {value: 'Tamamlandı', label: 'Tamamlandı'},
                          {value: 'Hepsi', label: 'Hepsi'},
                        ]}
                      />
                    </div>
                  </div>
                  <div>
                    <SelectInput
                      name='userPurpose'
                      label='Kullanım Amacı'
                      options={[
                        {value: 'İş', label: 'İş'},
                        {value: 'Özel', label: 'Özel'},
                        {value: 'Hepsi', label: 'Hepsi'},
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className='d-flex justify-content-between align-items-center mt-6'>
                <button type='submit' className='btn btn-primary'>
                  <i className='bi bi-funnel-fill me-2'></i>Filtrele
                </button>
                <span className='text-muted fw-bold mx-auto'>Kayıt Sayısı: {tableData.length}</span>
              </div>
            </Form>
          )}
        </Formik>

        <div className='mt-5'>
          <DataTable
            data={tableData}
            columns={bafColumns}
            actions={bafActions}
            showPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            loading={loading}
            emptyMessage={emptyMessage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSortLocal}
          />
        </div>
      </div>
    </div>
  )
}
